import React, {useState} from "react"
import {JwksPatternSection} from "./JwksPatternSection"
import {JWTProviders} from "../../../resources/orgSettings"
import {Modal} from "../../Modals/Modal"

interface Props {
	close: () => void
	provider: JWTProviders
}

// Provider-specific configurations
type ProviderConfig = {
	urlPattern: string
	steps: string[]
	additionalInfo?: {text: string; link?: string}
}

type SupportedProviders = Exclude<JWTProviders, JWTProviders.Custom>

const providerConfigs: Record<SupportedProviders, ProviderConfig> = {
	[JWTProviders.Auth0]: {
		urlPattern: "https://[YOUR_AUTH0_DOMAIN]/.well-known/jwks.json",
		steps: [
			"Log in to your Auth0 Dashboard",
			'Go to "Applications" > Select your application',
			'Go to "Settings" tab',
			'Find your "Domain" value',
			'Your JWKS URL is your domain + "/.well-known/jwks.json"',
		],
	},
	[JWTProviders.Cognito]: {
		urlPattern: "https://cognito-idp.[REGION].amazonaws.com/[USER_POOL_ID]/.well-known/jwks.json",
		steps: [
			"Go to the AWS Management Console",
			'Navigate to Amazon Cognito > "User Pools"',
			"Select your User Pool",
			'Go to "App integration" > "Domain name"',
			'Note your "User Pool ID" and "Region"',
			'Construct URL: "https://cognito-idp.[REGION].amazonaws.com/[USER_POOL_ID]/.well-known/jwks.json"',
		],
	},
	[JWTProviders.Okta]: {
		urlPattern: "https://[YOUR_OKTA_DOMAIN]/oauth2/v1/keys",
		steps: [
			"Log in to your Okta Admin Dashboard",
			'Go to "Security" > "API"',
			"Select the Authorization Server",
			'Look for "Metadata URI" and add "/keys" to the end OR',
			'Use the format: "https://[YOUR_OKTA_DOMAIN]/oauth2/v1/keys"',
		],
		additionalInfo: {
			text: "For custom authorization servers, the path may be:",
			link: "/oauth2/[AUTH_SERVER_ID]/v1/keys",
		},
	},
	[JWTProviders.Stytch]: {
		urlPattern: "https://api.stytch.com/v1/sessions/jwks",
		steps: [
			"Log in to your Stytch Dashboard",
			'Navigate to "API Keys" in the sidebar',
			'Your JWKS URL is standardized as "https://api.stytch.com/v1/sessions/jwks"',
		],
		additionalInfo: {text: "Stytch uses the same JWKS URL for all projects, but the keys are project-specific."},
	},
}

export function JwksInfoModal({close, provider: selectedProvider}: Props) {
	const [provider, setProvider] = useState<SupportedProviders>(selectedProvider as SupportedProviders)

	const handleProviderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setProvider(event.target.value as SupportedProviders)
	}

	const currentConfig = providerConfigs[provider]

	return (
		<Modal close={close} classname="jwks-info-modal">
			<div className="modal-body">
				<p className="modal-card-title">What is a JWKS?</p>
				<div className="jwks-info-content">
					<p className="description">
						JWKS (JSON Web Key Set) is a set of public keys used to verify the signatures of JSON Web Tokens (JWTs)
						issued by an identity provider. The JWKS endpoint exposes these keys, allowing your application to validate
						tokens without having to hardcode the keys.
					</p>

					<div className="provider-selector">
						<label className="label">Finding Your JWKS URL for</label>
						<div className="select">
							<select value={provider} onChange={handleProviderChange}>
								{Object.values(JWTProviders)
									.filter((p) => p !== JWTProviders.Custom)
									.map((p) => (
										<option key={p} value={p}>
											{p}
										</option>
									))}
							</select>
						</div>
					</div>

					<JwksPatternSection
						urlPattern={currentConfig.urlPattern}
						steps={currentConfig.steps}
						additionalInfo={currentConfig.additionalInfo}
					/>
				</div>
			</div>
		</Modal>
	)
}
